import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Password reset failed. The link may have expired.');
      }
    } catch {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 22px',
    borderRadius: 999,
    background: 'var(--hj-blue)',
    border: '1px solid var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)',
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    minHeight: 44,
  };

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <div className="w-full" style={{ maxWidth: 440 }}>
        <div className="text-center" style={{ marginBottom: 24 }}>
          <img
            src="/brand/wordmark-heyjoe.png"
            alt="Hey Joe Logo"
            className="mx-auto dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]"
            style={{
              height: 36,
              width: 'auto',
              marginBottom: 22,
            }}
          />
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.03em',
              fontWeight: 800,
            }}
          >
            {success ? 'All set.' : 'New password.'}
          </h1>
        </div>

        <div
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          {!token ? (
            <div className="text-center space-y-4">
              <AlertCircle className="h-10 w-10 mx-auto" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontSize: 14, color: 'var(--hj-ink-3)', lineHeight: 1.55 }}>
                Invalid or missing reset token. Request a new password reset link.
              </p>
              <button type="button" onClick={() => navigate('/auth')} style={primaryButtonStyle}>
                Go to sign in
              </button>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: 'var(--hj-green)' }} />
              <p style={{ fontSize: 14, color: 'var(--hj-ink-3)', lineHeight: 1.55 }}>
                Your password has been reset.
              </p>
              <button type="button" onClick={() => navigate('/auth')} style={primaryButtonStyle}>
                Sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  className="flex items-start gap-2"
                  style={{
                    padding: 12,
                    borderRadius: 'var(--hj-r-sm)',
                    background: 'oklch(0.30 0.12 25 / 0.18)',
                    border: '1px solid hsl(var(--destructive) / 0.5)',
                    color: 'hsl(var(--destructive))',
                    fontSize: 13,
                  }}
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" style={{ marginTop: 1 }} />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  minLength={6}
                  autoFocus
                />
                <p className="m-0" style={{ fontSize: 11, color: 'var(--hj-ink-4)' }}>
                  6+ characters.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Confirm new password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !password || !confirmPassword}
                className="inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                style={{
                  ...primaryButtonStyle,
                  opacity: isLoading || !password || !confirmPassword ? 0.5 : 1,
                  cursor: isLoading || !password || !confirmPassword ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Resetting…
                  </>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
